"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import type { ApiWave } from "@/generated/models/ApiWave";
import MemesArtSubmissionModal from "../MemesArtSubmissionModal";
import { SubmissionStatus, useWave } from "@/hooks/useWave";
interface MobileMemesArtSubmissionBtnProps {
  readonly wave: ApiWave;
  readonly isSubmissionLocked?: boolean | undefined;
}
const URGENT_DEADLINE_MS = 6 * 60 * 60 * 1000;
const URGENT_REFRESH_MS = 60_000;
interface MobileMemesArtSubmissionBtnContentProps {
  readonly ariaLabel: string;
  readonly isSubmissionDisabled: boolean;
  readonly isUrgent: boolean;
  readonly wave: ApiWave;
}
const MobileMemesArtSubmissionBtnContent: React.FC<MobileMemesArtSubmissionBtnContentProps> = t0 => {
  const $ = _c(19);
  const {
    ariaLabel,
    isSubmissionDisabled,
    isUrgent,
    wave
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] !== isSubmissionDisabled) {
    t1 = () => {
      if (isSubmissionDisabled) {
        return;
      }
      setIsOpen(true);
    };
    $[0] = isSubmissionDisabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isSubmissionDisabled && "tw-cursor-not-allowed tw-border-gray-400 tw-bg-gray-400";
  const t3 = !isSubmissionDisabled && isUrgent && "tw-animate-pulse tw-border-primary-600 tw-bg-primary-600";
  const t4 = !isSubmissionDisabled && !isUrgent && "tw-border-primary-500 tw-bg-primary-500 desktop-hover:hover:tw-border-primary-600 desktop-hover:hover:tw-bg-primary-600";
  let t5;
  if ($[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = clsx("tw-absolute tw-right-4 tw-top-4 tw-z-40 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-transition tw-duration-300 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-600", t2, t3, t4);
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <svg xmlns="http://www.w3.org/2000/svg" className="tw-size-6 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== ariaLabel || $[8] !== isSubmissionDisabled || $[9] !== t1 || $[10] !== t5) {
    t7 = <button onClick={t1} disabled={isSubmissionDisabled} className={t5} aria-label={ariaLabel}>{t6}</button>;
    $[7] = ariaLabel;
    $[8] = isSubmissionDisabled;
    $[9] = t1;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsOpen(false);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isOpen || $[14] !== wave) {
    t9 = <MemesArtSubmissionModal isOpen={isOpen} wave={wave} onClose={t8} />;
    $[13] = isOpen;
    $[14] = wave;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <div className="lg:tw-hidden">{t7}{t9}</div>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
const useCurrentMillis = enabled => {
  const $ = _c(3);
  const [currentMillis, setCurrentMillis] = useState(null);
  let t0;
  let t1;
  if ($[0] !== enabled) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      const updateCurrentMillis = () => {
        setCurrentMillis(Date.now());
      };
      const timeoutId = globalThis.setTimeout(updateCurrentMillis, 0);
      const intervalId = globalThis.setInterval(updateCurrentMillis, URGENT_REFRESH_MS);
      return () => {
        globalThis.clearTimeout(timeoutId);
        globalThis.clearInterval(intervalId);
      };
    };
    t1 = [enabled];
    $[0] = enabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return currentMillis;
};

/**
 * Floating button for mobile art submission in the top-right corner
 * Supports multiple states:
 * - Active: Green button when submissions are open and user can submit
 * - Disabled: Gray button when user can't submit (submissions ended, max reached, or not eligible)
 * - Urgent: Active button with animation when submission deadline is approaching
 */
const MobileMemesArtSubmissionBtn: React.FC<MobileMemesArtSubmissionBtnProps> = t0 => {
  const $ = _c(15);
  const {
    wave,
    isSubmissionLocked: t1
  } = t0;
  const isSubmissionLocked = t1 === undefined ? false : t1;
  const waveInfo = useWave(wave);
  const canSubmit = waveInfo.participation.canSubmitNow === true;
  const isSubmissionDisabled = isSubmissionLocked || !canSubmit;
  const shouldTrackUrgency = canSubmit && !isSubmissionLocked && waveInfo.participation.isWithinPeriod === true;
  const currentMillis = useCurrentMillis(shouldTrackUrgency);
  const timeRemaining = currentMillis === null ? null : waveInfo.participation.endTime - currentMillis;
  let t2;
  if ($[0] !== shouldTrackUrgency || $[1] !== timeRemaining) {
    t2 = shouldTrackUrgency && timeRemaining !== null && Number.isFinite(timeRemaining) && timeRemaining > 0 && timeRemaining < URGENT_DEADLINE_MS;
    $[0] = shouldTrackUrgency;
    $[1] = timeRemaining;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isUrgent = t2;
  let t3;
  if ($[3] !== isSubmissionLocked || $[4] !== isUrgent || $[5] !== waveInfo.participation.hasReachedLimit || $[6] !== waveInfo.participation.isEligible || $[7] !== waveInfo.participation.status) {
    t3 = () => {
      if (isSubmissionLocked) {
        return "Submissions are closed";
      }
      const status = waveInfo.participation.status;
      if (status === SubmissionStatus.ENDED) {
        return "Submissions are closed";
      }
      if (!waveInfo.participation.isEligible) {
        return "You are not eligible to submit to this wave";
      }
      if (waveInfo.participation.hasReachedLimit) {
        return "You have reached your maximum submissions";
      }
      return isUrgent ? "Submit Work to The Memes - Deadline approaching!" : "Submit Work to The Memes";
    };
    $[3] = isSubmissionLocked;
    $[4] = isUrgent;
    $[5] = waveInfo.participation.hasReachedLimit;
    $[6] = waveInfo.participation.isEligible;
    $[7] = waveInfo.participation.status;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getAriaLabel = t3;
  const t4 = isSubmissionLocked ? "locked" : "unlocked";
  const t5 = getAriaLabel();
  let t6;
  if ($[9] !== isSubmissionDisabled || $[10] !== isUrgent || $[11] !== t4 || $[12] !== t5 || $[13] !== wave) {
    t6 = <MobileMemesArtSubmissionBtnContent key={t4} ariaLabel={t5} isSubmissionDisabled={isSubmissionDisabled} isUrgent={isUrgent} wave={wave} />;
    $[9] = isSubmissionDisabled;
    $[10] = isUrgent;
    $[11] = t4;
    $[12] = t5;
    $[13] = wave;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export default React.memo(MobileMemesArtSubmissionBtn);